export function detectFormat(content, filename) {
  const trimmed = content.trim();

  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    return 'json';
  }
  const firstLine = trimmed.split('\n')[0] || '';
  if (firstLine.includes('\t')) return 'tsv';
  if (firstLine.includes(';')) return 'csv';
  if (firstLine.includes(',')) return 'csv';
  return 'unknown';
}

export function detectSource(content, filename) {
  const lower = (filename || '').toLowerCase();
  const first500 = content.slice(0, 500).toLowerCase();

  if (lower.includes('entra') || lower.includes('aad') || lower.includes('ms') || lower.includes('microsoft')) {
    return 'entraid';
  }
  if (lower.includes('google') || lower.includes('workspace') || lower.includes('gsuite') || lower.includes('gws')) {
    return 'google';
  }

  if (first500.includes('createddatetime') || first500.includes('userprincipalname') || first500.includes('appdisplayname')) {
    return 'entraid';
  }
  if (first500.includes('actor.email') || first500.includes('actor') || first500.includes('events')) {
    return 'google';
  }

  return 'unknown';
}

export function parseEntraIDJson(jsonData) {
  const records = Array.isArray(jsonData) ? jsonData : [jsonData];
  return records.map((r, idx) => ({
    timestamp: r.createdDateTime || r.CreatedDateTime || '',
    user: r.userPrincipalName || r.userprincipalname || r.UserPrincipalName || r.userDisplayName || '',
    ip: r.ipAddress || r.IPAddress || r.clientIpAddress || '',
    app: r.appDisplayName || r.AppDisplayName || '',
    clientApp: r.clientAppUsed || r.ClientAppUsed || '',
    location: r.location?.city || r.Location?.city || '',
    state: r.location?.state || r.Location?.state || '',
    country: r.location?.countryOrRegion || r.Location?.countryOrRegion || '',
    result: r.status?.errorCode !== undefined ? (r.status.errorCode === 0 ? 'Success' : `Failure ${r.status.errorCode}`) : '',
    risk: r.riskLevelAggregated || r.riskLevelDuringSignIn || '',
    deviceId: r.deviceDetail?.deviceId || '',
    device: r.deviceDetail?.displayName || '',
    mfa: r.mfaDetail || r.authenticationRequirement || '',
    operatingSystem: r.deviceDetail?.operatingSystem || '',
    browser: r.deviceDetail?.browser || '',
    raw: r,
  })).filter(r => r.ip && r.ip !== '');
}

export function parseGoogleWorkspaceJson(jsonData) {
  const records = Array.isArray(jsonData) ? jsonData : [jsonData];
  return records.map((r, idx) => {
    const events = r.events || [];
    const loginEvent = events.find(e => e.name === 'login' || e.name === 'login_success' || e.name === 'login_failure' || e.name === 'login_challenge') || events[0] || {};
    const params = loginEvent.parameters || [];
    const getParam = (name) => {
      const p = params.find(p => p.name === name);
      return p?.value || p?.boolValue || '';
    };

    return {
      timestamp: r.id?.time || '',
      user: r.actor?.email || '',
      ip: r.ipAddress || '',
      app: getParam('application_name') || getParam('login_type') || '',
      clientApp: '',
      location: '',
      state: '',
      country: '',
      result: loginEvent.name?.includes('failure') ? 'Failure' : loginEvent.name?.includes('success') ? 'Success' : loginEvent.name || '',
      risk: '',
      device: getParam('device_id') || '',
      mfa: getParam('login_challenge_method') || '',
      operatingSystem: getParam('os_platform') || '',
      browser: getParam('browser') || '',
      raw: r,
    };
  }).filter(r => r.ip && r.ip !== '');
}

export function parseCSV(content) {
  const lines = content.trim().split('\n');
  if (lines.length < 2) return [];

  const firstLine = lines[0] || '';
  const separator = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ',';
  const headerLine = lines[0].trim();
  const headers = headerLine.split(separator).map(h => h.trim().replace(/^"|"$/g, ''));

  const results = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = parseCSVLine(line, separator);
    if (values.length === 0 || values.every(v => !v)) continue;

    const record = {};
    headers.forEach((h, idx) => {
      record[h] = values[idx] || '';
    });
    results.push(record);
  }

  return results;
}

function parseCSVLine(line, separator) {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === separator && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  values.push(current);
  return values;
}

export function normalizeRecords(parsedRecords, source) {
  const fieldMap = {
    'timestamp': ['timestamp', 'createddatetime', 'created_date_time', 'createddate', 'time', 'date', 'datetime', 'createdat', 'activitydate', 'activity_date', 'date_utc', 'dateutc', 'signindate', 'signin_date', 'login_date', 'report_date'],
    'user': ['user', 'userprincipalname', 'user_principal_name', 'actor.email', 'email', 'username', 'userdisplayname', 'userdisplayname', 'actor', 'userid'],
    'ip': ['ip', 'ipaddress', 'ip_address', 'clientip', 'client_ip', 'sourceip', 'source_ip', 'srcip', 'src_ip'],
    'app': ['app', 'appdisplayname', 'application_name', 'app_name', 'application', 'clientapp'],
    'result': ['result', 'status', 'errorcode', 'login_result', 'loginstatus', 'signinstatus'],
    'risk': ['risk', 'risklevel', 'risk_level_aggregated', 'risk_level', 'risklevelduringlogin'],
    'country': ['country', 'country_region', 'countryorregion', 'countryregion'],
    'state': ['state', 'region', 'province'],
    'location': ['location', 'city', 'locality'],
    'deviceId': ['deviceid', 'device_id', 'device-id'],
  };

  // Pre-build alias → target map: O(m·k) once, then O(1) lookups per field
  const aliasMap = new Map();
  for (const [target, aliases] of Object.entries(fieldMap)) {
    for (const alias of aliases) {
      aliasMap.set(alias, target);
    }
  }

  return parsedRecords.map(record => {
    const normalized = { source };

    // Single pass over record keys with O(1) alias lookup
    for (const [key, value] of Object.entries(record)) {
      const keyLower = key.toLowerCase().replace(/[^a-z0-9_.]/g, '');
      const target = aliasMap.get(keyLower);
      if (target && !(target in normalized)) {
        normalized[target] = value;
      }
    }

    if (!normalized.ip) {
      for (const v of Object.values(record)) {
        const s = String(v);
        if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(s.trim())) {
          normalized.ip = s.trim();
          break;
        }
      }
    }

    // Prefer specific UPN-format user identifiers over generic User/UserID columns
    // Aliases listed in priority order: Username/UPN > email > displayname > User > UserID
    if (normalized.user) {
      const preferredUserAliases = ['username', 'userprincipalname', 'user_principal_name', 'actor.email', 'email', 'userdisplayname'];
      for (const [key, value] of Object.entries(record)) {
        if (!value) continue;
        const keyLower = key.toLowerCase().replace(/[^a-z0-9_.]/g, '');
        if (preferredUserAliases.includes(keyLower)) {
          normalized.user = value;
          break;
        }
      }
    }

    normalized.timestamp = normalized.timestamp || '';
    normalized.user = normalized.user || '';
    normalized.ip = normalized.ip || '';
    normalized.app = normalized.app || '';
    normalized.result = normalized.result || '';
    normalized.risk = normalized.risk || '';
    normalized.country = normalized.country || '';

    return normalized;
  }).filter(r => r.ip);
}

export async function parseFile(file) {
  let content = await file.text();
  content = content.replace(/^\ufeff/, '');
  const format = detectFormat(content, file.name);
  const detectedSource = detectSource(content, file.name);
  let records;

  if (format === 'json') {
    const jsonData = JSON.parse(content);
    if (detectedSource === 'google') {
      records = parseGoogleWorkspaceJson(jsonData);
    } else {
      records = parseEntraIDJson(jsonData);
    }
  } else if (format === 'csv' || format === 'tsv') {
    const rows = parseCSV(content);
    records = normalizeRecords(rows, detectedSource);
  } else {
    throw new Error(`Unrecognized format: ${format}`);
  }

  const enriched = records.map(r => {
    let ts = r.timestamp ? new Date(r.timestamp) : null;
    if (!ts || isNaN(ts.getTime())) ts = null;
    return {
      ...r,
      timestamp: r.timestamp || (ts ? ts.toISOString() : new Date().toISOString()),
      user: r.user || 'unknown',
      ip: r.ip.replace(/^::ffff:/, ''),
      status: 'unreviewed',
      source: detectedSource,
    };
  });

  return enriched;
}
